import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Modal,
  ModalDialog,
  Stack,
  Step,
  Stepper,
  Typography,
} from "@mui/joy";
import CheckIcon from "@mui/icons-material/Check";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import CloseIcon from "@mui/icons-material/Close";
import { FileRejection, useDropzone } from "react-dropzone";
import Page from "../../../../components/Pages/Page";
import PageHeader from "../../../../components/PageHeader";
import SubTitle from "../../../../components/SubTitle";
import useQualityCheck from "../../hooks/useQualityCheck";
import useAuthToken from "../../../../utils/useAuthToken";
import { QCSectionResult, QCStatus } from "../../types/qcTypes";
import QCProgressView from "../../components/qc/QCProgressView";
import IndependentQCDocumentView from "../../components/qc/IndependentQCDocumentView";
import QCSectionErrorNotice from "../../components/qc/QCSectionErrorNotice";
import { useWorkspaceContext } from "../../../../providers/WorkspaceProvider";
import { getFriendlyQCErrorMessage } from "../../utils/qcErrorMessages";
import {
  isPartiallyChecked,
  isUnchecked,
  partialSectionMessage,
  recoverSectionsFromRun,
} from "../../utils/qcPartialSections";
import { severityLabel } from "../../components/qc/qcRedlineShared";

type WizardStep = "upload" | "check" | "review";

const STEPS: { key: WizardStep; label: string }[] = [
  { key: "upload", label: "Upload" },
  { key: "check", label: "Check" },
  { key: "review", label: "Review" },
];

export const ACCEPTED_FILE_TYPES = {
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "text/plain": [".txt"],
  "text/markdown": [".md"],
};

// ---------------------------------------------------------------------------
// Upload helpers
// ---------------------------------------------------------------------------

const ACCEPTED_EXTENSIONS = ".pdf, .doc, .docx, .txt or .md";

// Identifies a picked file, so the same file isn't added twice and list rows
// keep their identity when one is removed.
export const fileKey = (file: File) =>
  `${file.name}:${file.size}:${file.lastModified}`;

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const describeRejections = (rejections: FileRejection[]): string[] =>
  rejections.map(({ file, errors }) => {
    if (errors.some((e) => e.code === "too-many-files")) {
      return "Add one document to check at a time.";
    }
    if (errors.some((e) => e.code === "file-invalid-type")) {
      return `${file.name} isn't a supported file. Use ${ACCEPTED_EXTENSIONS}.`;
    }
    return `${file.name} couldn't be added: ${errors[0]?.message ?? "unknown error"}.`;
  });

interface FileDropzoneProps {
  label: string;
  multiple: boolean;
  onFiles: (files: File[]) => void;
}

// A drop area that also says which dropped files it refused, and why.
const FileDropzone: React.FC<FileDropzoneProps> = ({
  label,
  multiple,
  onFiles,
}) => {
  const [rejected, setRejected] = useState<string[]>([]);
  const dropzone = useDropzone({
    onDrop: (accepted: File[], rejections: FileRejection[] = []) => {
      setRejected(describeRejections(rejections));
      if (accepted.length > 0) onFiles(accepted);
    },
    accept: ACCEPTED_FILE_TYPES,
    multiple,
    ...(multiple ? {} : { maxFiles: 1 }),
  });

  return (
    <>
      <Box
        {...dropzone.getRootProps()}
        sx={{
          border: "2px dashed",
          borderColor: dropzone.isDragActive
            ? "primary.outlinedHoverBorder"
            : "neutral.outlinedBorder",
          bgcolor: dropzone.isDragActive ? "primary.softBg" : undefined,
          borderRadius: "sm",
          p: 3,
          textAlign: "center",
          cursor: "pointer",
          transition: "border-color 0.2s ease, background-color 0.2s ease",
          "&:hover": {
            borderColor: "primary.outlinedHoverBorder",
            bgcolor: "primary.softBg",
          },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "focusVisible",
          },
        }}
      >
        <input {...dropzone.getInputProps()} aria-label={label} />
        <CloudUploadIcon sx={{ fontSize: 32, color: "text.tertiary", mb: 1 }} />
        <Typography level="body-sm" color="neutral">
          Drag and drop or click to choose {multiple ? "files" : "a file"} (
          {ACCEPTED_EXTENSIONS})
        </Typography>
      </Box>
      {rejected.length > 0 && (
        <Stack spacing={0.25} role="alert" sx={{ mt: 1 }}>
          {rejected.map((message) => (
            <Typography key={message} level="body-sm" color="danger">
              {message}
            </Typography>
          ))}
        </Stack>
      )}
    </>
  );
};

const FileRow: React.FC<{ file: File; onRemove: () => void }> = ({
  file,
  onRemove,
}) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    spacing={1}
    sx={{ py: 0.5 }}
  >
    <Typography level="body-sm" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
      {file.name}
      <Typography
        component="span"
        level="body-xs"
        color="neutral"
        sx={{ ml: 1 }}
      >
        {formatFileSize(file.size)}
      </Typography>
    </Typography>
    <IconButton size="sm" aria-label={`Remove ${file.name}`} onClick={onRemove}>
      <CloseIcon />
    </IconButton>
  </Stack>
);

// ---------------------------------------------------------------------------
// UploadStep
// ---------------------------------------------------------------------------

interface UploadStepProps {
  documentFile: File | null;
  sourceFiles: File[];
  onDocumentDrop: (files: File[]) => void;
  onSourcesDrop: (files: File[]) => void;
  onRemoveSource: (key: string) => void;
  onStart: () => void;
  loading: boolean;
  /** Why the last start failed, if it did. */
  error: string | null;
}

const UploadStep: React.FC<UploadStepProps> = ({
  documentFile,
  sourceFiles,
  onDocumentDrop,
  onSourcesDrop,
  onRemoveSource,
  onStart,
  loading,
  error,
}) => {
  const missing = !documentFile
    ? sourceFiles.length === 0
      ? "Add a document to check and at least one source to start."
      : "Add a document to check to start."
    : sourceFiles.length === 0
      ? "Add at least one source document to start."
      : null;

  return (
    <Stack spacing={3}>
      <Card variant="outlined">
        <Typography level="title-md" sx={{ mb: 1 }}>
          Document to check
        </Typography>
        <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
          Upload the document you want to verify against source materials.
        </Typography>
        <FileDropzone
          label="Choose the document to check"
          multiple={false}
          onFiles={onDocumentDrop}
        />
        {documentFile && (
          <Box sx={{ mt: 1 }}>
            <FileRow file={documentFile} onRemove={() => onDocumentDrop([])} />
          </Box>
        )}
      </Card>

      <Card variant="outlined">
        <Typography level="title-md" sx={{ mb: 1 }}>
          Source documents
        </Typography>
        <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
          Upload the source materials to verify the document against.
        </Typography>
        <FileDropzone
          label="Choose source documents"
          multiple
          onFiles={onSourcesDrop}
        />
        {sourceFiles.length > 0 && (
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {sourceFiles.map((file) => (
              <FileRow
                key={fileKey(file)}
                file={file}
                onRemove={() => onRemoveSource(fileKey(file))}
              />
            ))}
          </Stack>
        )}
      </Card>

      {error && (
        <Alert color="danger" variant="soft">
          {getFriendlyQCErrorMessage(error)}
        </Alert>
      )}

      <Stack spacing={1} alignItems="center">
        <Button
          size="lg"
          variant="solid"
          color="primary"
          onClick={onStart}
          disabled={missing !== null || loading}
          startDecorator={
            loading ? <CircularProgress size="sm" /> : <FactCheckIcon />
          }
          aria-describedby={missing ? "qc-start-hint" : undefined}
          sx={{ minWidth: 200 }}
        >
          {loading ? "Starting…" : "Start quality check"}
        </Button>
        {missing && (
          <Typography id="qc-start-hint" level="body-sm" color="neutral">
            {missing}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Findings report (any file type)
// ---------------------------------------------------------------------------

const RESOLUTION_LABEL = {
  accepted: "Accepted",
  declined: "Declined",
} as const;

// A Markdown report of every finding, so results can be kept and shared for
// any document type, not only .docx (which also gets track changes).
export const buildFindingsReport = (
  documentName: string,
  sections: QCSectionResult[],
  checkedAt: Date,
): string => {
  const total = sections.reduce((n, s) => n + s.findings.length, 0);
  const lines = [
    `# Quality check findings: ${documentName}`,
    "",
    `Checked ${checkedAt.toLocaleString()}. ${total} finding${
      total === 1 ? "" : "s"
    } in ${sections.length} section${sections.length === 1 ? "" : "s"}.`,
  ];
  for (const section of sections) {
    lines.push("", `## ${section.sectionTitle || "Untitled section"}`, "");
    if (section.status === "error" && section.partial) {
      lines.push(partialSectionMessage(section.partial), "");
    } else if (section.status === "error") {
      lines.push("This section could not be checked.");
      continue;
    }
    if (section.findings.length === 0) {
      lines.push("No issues found.");
      continue;
    }
    section.findings.forEach((f, i) => {
      const resolution = f.Resolution
        ? RESOLUTION_LABEL[f.Resolution]
        : "Pending";
      lines.push(
        `### ${i + 1}. ${severityLabel[f.Severity]} · ${f.FindingType.replace(
          "_",
          " ",
        )} · ${resolution}`,
        "",
        `- Original: "${f.OriginalText}"`,
      );
      const replacement = f.EditedText ?? f.SuggestedText;
      if (replacement != null) {
        lines.push(
          `- ${f.EditedText != null ? "Your edit" : "Suggested"}: ${
            replacement === "" ? "(delete)" : `"${replacement}"`
          }`,
        );
      }
      lines.push(`- Why: ${f.Explanation}`);
      if (f.SourceName) {
        lines.push(
          `- Source: ${f.SourceName}${
            f.SourceSnippet ? ` ("${f.SourceSnippet}")` : ""
          }`,
        );
      }
      lines.push("");
    });
  }
  return lines.join("\n");
};

const downloadText = (fileName: string, text: string) => {
  const url = URL.createObjectURL(
    new Blob([text], { type: "text/markdown;charset=utf-8" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------------------
// ReviewStep
// ---------------------------------------------------------------------------

interface ReviewStepProps {
  sections: QCSectionResult[];
  totalFindings: number;
  documentName: string;
  onAcceptFinding: (findingId: number, editedText?: string | null) => void;
  onDeclineFinding: (findingId: number) => void;
  onRevertFinding: (findingId: number) => void;
  onSubmitFeedback: (findingId: number, helpful: boolean) => void;
  // Document-level bulk handlers (no sectionIndex -- operate across the
  // entire document). The continuous-document layout collapses what used to
  // be per-section bulk buttons into a single top toolbar.
  onAcceptAll: () => void;
  onDeclineAll: () => void;
  onRevertAll: () => void;
  onReset: () => void;
  isDocx: boolean;
  onDownload: () => void;
  downloadLoading: boolean;
}

const SummaryFigure: React.FC<{
  value: number;
  label: string;
  color?: "danger" | "warning";
}> = ({ value, label, color }) => (
  <Box role="group" aria-label={label} sx={{ textAlign: "center" }}>
    <Typography level="h4" color={color}>
      {value}
    </Typography>
    <Typography level="body-sm">{label}</Typography>
  </Box>
);

const ReviewStep: React.FC<ReviewStepProps> = ({
  sections,
  totalFindings,
  documentName,
  onAcceptFinding,
  onDeclineFinding,
  onRevertFinding,
  onSubmitFeedback,
  onAcceptAll,
  onDeclineAll,
  onRevertAll,
  onReset,
  isDocx,
  onDownload,
  downloadLoading,
}) => {
  const [confirmingReset, setConfirmingReset] = useState(false);
  const sectionsWithErrors = sections.filter((s) => s.status === "error");
  const uncheckedSections = sections.filter(isUnchecked);
  const partialSections = sections.filter(isPartiallyChecked);
  const checkedSections = sections.filter((s) => s.status === "complete");
  const cleanSections = checkedSections.filter((s) => s.findingCount === 0);
  const unresolved = sections.reduce(
    (n, s) => n + s.findings.filter((f) => f.Resolution === null).length,
    0,
  );

  const requestReset = () => {
    if (unresolved > 0) setConfirmingReset(true);
    else onReset();
  };

  const downloadReport = () => {
    const base = documentName.replace(/\.[^.]+$/, "") || "document";
    downloadText(
      `${base} - quality check findings.md`,
      buildFindingsReport(documentName, sections, new Date()),
    );
  };

  return (
    <Box>
      {/* Summary */}
      <Card variant="soft" sx={{ mb: 3 }}>
        <Stack
          direction="row"
          spacing={3}
          justifyContent="center"
          flexWrap="wrap"
          useFlexGap
        >
          <SummaryFigure
            value={checkedSections.length}
            label="Sections checked"
          />
          <SummaryFigure value={totalFindings} label="Findings" />
          <SummaryFigure value={cleanSections.length} label="Clean sections" />
          {partialSections.length > 0 && (
            <SummaryFigure
              value={partialSections.length}
              label="Partially checked"
              color="warning"
            />
          )}
          {uncheckedSections.length > 0 && (
            <SummaryFigure
              value={uncheckedSections.length}
              label="Couldn't check"
              color="danger"
            />
          )}
        </Stack>
      </Card>

      {/* Results */}
      {totalFindings === 0 && sectionsWithErrors.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography level="h4" sx={{ mb: 1 }}>
            No issues found
          </Typography>
          <Typography level="body-md" color="neutral">
            The document content appears consistent with the source materials.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {sectionsWithErrors.length > 0 && (
            <Alert color="warning" variant="soft">
              <QCSectionErrorNotice sections={sectionsWithErrors} />
            </Alert>
          )}
          <IndependentQCDocumentView
            sections={sections}
            onAcceptFinding={onAcceptFinding}
            onDeclineFinding={onDeclineFinding}
            onRevertFinding={onRevertFinding}
            onSubmitFeedback={onSubmitFeedback}
            onAcceptAll={onAcceptAll}
            onDeclineAll={onDeclineAll}
            onRevertAll={onRevertAll}
            readOnly={false}
          />
        </Stack>
      )}

      {/* Actions */}
      <Divider sx={{ my: 3 }} />
      <Stack
        direction="row"
        spacing={2}
        justifyContent="center"
        flexWrap="wrap"
        useFlexGap
      >
        <Button variant="outlined" color="neutral" onClick={requestReset}>
          Start new check
        </Button>
        <Button
          variant={isDocx ? "outlined" : "solid"}
          color="primary"
          onClick={downloadReport}
          startDecorator={<DownloadIcon />}
        >
          Download findings report
        </Button>
        {isDocx && (
          <Button
            variant="solid"
            color="primary"
            onClick={onDownload}
            loading={downloadLoading}
            startDecorator={<DownloadIcon />}
          >
            Download with track changes
          </Button>
        )}
      </Stack>

      <Modal open={confirmingReset} onClose={() => setConfirmingReset(false)}>
        <ModalDialog role="alertdialog" aria-labelledby="qc-reset-title">
          <DialogTitle id="qc-reset-title">Discard this review?</DialogTitle>
          <DialogContent>
            {unresolved} finding{unresolved === 1 ? " is" : "s are"} still
            unresolved. Starting a new check closes this review and you can't
            come back to it.
          </DialogContent>
          <DialogActions>
            <Button
              color="danger"
              onClick={() => {
                setConfirmingReset(false);
                onReset();
              }}
            >
              Discard and start new check
            </Button>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setConfirmingReset(false)}
            >
              Keep reviewing
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// IndependentQC (main component)
// ---------------------------------------------------------------------------

const IndependentQC: React.FC = () => {
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";
  // Auth token for SSE stream (EventSource cannot set headers)
  const getAuthToken = useAuthToken();

  // Wizard state
  const [step, setStep] = useState<WizardStep>("upload");

  // Upload state
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [sourceFiles, setSourceFiles] = useState<File[]>([]);

  // QC state
  const [qcRunId, setQcRunId] = useState<number | null>(null);
  const [qcStatus, setQcStatus] = useState<QCStatus>("pending");
  // Findings already counted in totalFindings, so one sent again is not.
  const countedFindingsRef = useRef<Set<number>>(new Set());
  const [sectionResults, setSectionResults] = useState<
    Map<number, QCSectionResult>
  >(new Map());
  const [totalFindings, setTotalFindings] = useState(0);
  const [totalSections, setTotalSections] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // SSE reconnect progress shown on the Check step; cleared by any event.
  const [reconnect, setReconnect] = useState<{
    attempt: number;
    max: number;
  } | null>(null);

  const {
    startIndependentQC,
    connectToStream,
    acceptFinding,
    declineFinding,
    revertFinding,
    bulkResolve,
    submitFeedback,
    downloadTrackedDocument,
    downloadLoading,
    disconnect,
    cancel,
    stop,
    loading,
  } = useQualityCheck();

  // Clean up SSE on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  // --- Upload handlers ---

  const handleDocumentDrop = useCallback((acceptedFiles: File[]) => {
    setErrorMessage(null);
    if (acceptedFiles.length > 0) {
      setDocumentFile(acceptedFiles[0]);
    } else {
      setDocumentFile(null);
    }
  }, []);

  const handleSourcesDrop = useCallback((acceptedFiles: File[]) => {
    setErrorMessage(null);
    setSourceFiles((prev) => {
      const known = new Set(prev.map(fileKey));
      return [...prev, ...acceptedFiles.filter((f) => !known.has(fileKey(f)))];
    });
  }, []);

  const removeSourceFile = (key: string) => {
    setSourceFiles((prev) => prev.filter((f) => fileKey(f) !== key));
  };

  // --- Start QC ---

  const handleStartQC = async () => {
    if (!documentFile || sourceFiles.length === 0) return;
    if (qcStatus === "running" || loading) return; // Prevent double execution

    const formData = new FormData();
    formData.append("document_file", documentFile);
    sourceFiles.forEach((file) => {
      formData.append("source_files", file);
    });

    setErrorMessage(null);
    try {
      const { qc_run_id } = await startIndependentQC(formData, workspaceId);
      setQcRunId(qc_run_id);
      setQcStatus("running");
      setStep("check");

      const token = await getAuthToken();
      connectToStream(qc_run_id, token, workspaceId, {
        onStarted: (total) => {
          setTotalSections(total);
          setReconnect(null);
        },
        onSectionStart: (index, title, content, sectionId) => {
          setReconnect(null);
          setSectionResults((prev) => {
            const next = new Map(prev);
            next.set(index, {
              sectionIndex: index,
              sectionId,
              sectionTitle: title,
              sectionContent: content,
              status: "checking",
              findings: [],
              findingCount: 0,
            });
            return next;
          });
        },
        onFinding: (finding) => {
          // A reconnect that follows the run sends finished sections again
          // with their findings: count each finding once.
          const counted = countedFindingsRef.current.has(finding.FindingID);
          countedFindingsRef.current.add(finding.FindingID);
          setSectionResults((prev) => {
            const next = new Map(prev);
            const section = next.get(finding.SectionIndex);
            if (section) {
              next.set(finding.SectionIndex, {
                ...section,
                findings: [...section.findings, finding],
              });
            }
            return next;
          });
          if (!counted) setTotalFindings((prev) => prev + 1);
        },
        onSectionComplete: (index, count) => {
          setSectionResults((prev) => {
            const next = new Map(prev);
            const section = next.get(index);
            if (section) {
              next.set(index, {
                ...section,
                status: "complete" as const,
                findingCount: count,
              });
            }
            return next;
          });
        },
        onSectionError: (index, count, error, section) => {
          setErrorMessage(error);
          setSectionResults((prev) => {
            const next = new Map(prev);
            const existing = next.get(index);
            next.set(index, {
              sectionIndex: index,
              sectionId: section?.sectionId ?? existing?.sectionId,
              sectionTitle:
                section?.sectionTitle ||
                existing?.sectionTitle ||
                "Unknown section",
              sectionContent: existing?.sectionContent || "",
              findings: existing?.findings || [],
              findingCount: count,
              status: "error",
              errorMessage: error,
              partial: section?.partial,
            });
            return next;
          });
        },
        onComplete: (total) => {
          setReconnect(null);
          setQcStatus("completed");
          setTotalFindings(total);
          setStep("review");
        },
        onError: (error, section) => {
          setReconnect(null);
          setQcStatus("failed");
          setErrorMessage(error);
          if (section?.sectionIndex !== undefined) {
            const failedSectionIndex = section.sectionIndex;
            setSectionResults((prev) => {
              const next = new Map(prev);
              const existing = next.get(failedSectionIndex);
              next.set(failedSectionIndex, {
                sectionIndex: failedSectionIndex,
                sectionId: section.sectionId,
                sectionTitle:
                  section.sectionTitle ||
                  existing?.sectionTitle ||
                  "Unknown section",
                sectionContent: existing?.sectionContent || "",
                findings: existing?.findings || [],
                findingCount: existing?.findingCount || 0,
                status: "error",
                errorMessage: error,
              });
              return next;
            });
          }
        },
        onReconnecting: (attempt, max) => {
          setReconnect({ attempt, max });
        },
        onRecoveredViaPoll: (run) => {
          // The stream died but the run finished: the server's findings and
          // each section's recorded outcome (complete, partly checked, or
          // failed) replace whatever had streamed in.
          setSectionResults((prev) => recoverSectionsFromRun(prev, run));
        },
      });
    } catch (err) {
      setQcStatus("failed");
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to start QC",
      );
    }
  };

  // --- Finding handlers ---

  const handleAcceptFinding = async (
    findingId: number,
    editedText?: string | null,
  ) => {
    if (!qcRunId) return;
    await acceptFinding(qcRunId, findingId, workspaceId, editedText);
    updateFindingResolution(findingId, "accepted", editedText);
  };

  const handleDeclineFinding = async (findingId: number) => {
    if (!qcRunId) return;
    await declineFinding(qcRunId, findingId, workspaceId);
    updateFindingResolution(findingId, "declined");
  };

  const handleRevertFinding = async (findingId: number) => {
    if (!qcRunId) return;
    await revertFinding(qcRunId, findingId, workspaceId);
    updateFindingResolution(findingId, null);
  };

  const updateFindingResolution = (
    findingId: number,
    resolution: "accepted" | "declined" | null,
    editedText?: string | null,
  ) => {
    setSectionResults((prev) => {
      const next = new Map(prev);
      for (const [key, section] of next) {
        const finding = section.findings.find((f) => f.FindingID === findingId);
        if (finding) {
          finding.Resolution = resolution;
          if (resolution === null) {
            finding.ResolvedDate = null;
            finding.EditedText = null;
          } else if (editedText != null) {
            finding.EditedText = editedText;
          }
          finding.Helpful = null;
          next.set(key, { ...section, findings: [...section.findings] });
          break;
        }
      }
      return next;
    });
  };

  // Bulk-resolve every finding in the document. The continuous-document view
  // has no per-section bulk affordances; the top toolbar's Accept All /
  // Decline All / Revert All all hit this with `sectionIndex: null` which the
  // backend treats as "all sections" (see qc.py bulk_resolve_findings).
  const handleDocumentBulkResolve = async (
    resolution: "accepted" | "declined" | null,
  ) => {
    if (!qcRunId) return;
    await bulkResolve(qcRunId, resolution, workspaceId, null);
    setSectionResults((prev) => {
      const next = new Map(prev);
      for (const [key, section] of next) {
        if (section.findings.length === 0) continue;
        next.set(key, {
          ...section,
          findings: section.findings.map((f) => ({
            ...f,
            Resolution: resolution,
            ResolvedDate: resolution === null ? null : f.ResolvedDate,
            Helpful: null,
            EditedText: resolution === null ? null : f.EditedText,
          })),
        });
      }
      return next;
    });
  };

  const handleSubmitFeedback = async (findingId: number, helpful: boolean) => {
    if (!qcRunId) return;
    await submitFeedback(qcRunId, findingId, helpful, workspaceId);
    setSectionResults((prev) => {
      const next = new Map(prev);
      for (const [key, section] of next) {
        const finding = section.findings.find((f) => f.FindingID === findingId);
        if (finding) {
          finding.Helpful = helpful;
          next.set(key, { ...section, findings: [...section.findings] });
          break;
        }
      }
      return next;
    });
  };

  // --- Reset ---

  const resetRunState = () => {
    countedFindingsRef.current = new Set();
    setQcRunId(null);
    setQcStatus("pending");
    setSectionResults(new Map());
    setTotalFindings(0);
    setTotalSections(0);
    setErrorMessage(null);
    setReconnect(null);
  };

  const handleReset = () => {
    // Start over abandons the run: cancel it so an unfinished extraction does
    // not keep the server's extraction slot busy.
    cancel();
    resetRunState();
    setStep("upload");
    setDocumentFile(null);
    setSourceFiles([]);
  };

  // Explicit Cancel while checking: stop the run (sections already checked
  // keep their findings server-side) and go back to the files, kept as they
  // are, so the check can be adjusted and started again.
  const handleStopCheck = () => {
    stop();
    resetRunState();
    setStep("upload");
  };

  // Check the same files again after a failed run.
  const handleRetry = () => {
    cancel();
    resetRunState();
    void handleStartQC();
  };

  // Convert map to sorted array
  const sortedSections = Array.from(sectionResults.values()).sort(
    (a, b) => a.sectionIndex - b.sectionIndex,
  );

  const activeStep = STEPS.findIndex((s) => s.key === step);

  return (
    <Page>
      <PageHeader>
        <Typography level="h1">Quality Check</Typography>
      </PageHeader>
      <SubTitle>Check a document against its source materials</SubTitle>
      <Card>
        <CardContent>
          {/* Same step look as the Document Type wizard: the current step is
              primary, finished steps are green with a check. Labels are not
              buttons, since the steps can't be jumped between. */}
          <Stepper
            sx={{
              mb: 3,
              "--Stepper-verticalGap": "0",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {STEPS.map((s, i) => {
              const completed = i < activeStep;
              const active = i === activeStep;
              // solidBg/solidColor are the theme's paired fill and text for
              // each colour, so the digit stays readable in every brand scheme.
              const tone = active
                ? "primary"
                : completed
                  ? "success"
                  : "neutral";
              return (
                <Step
                  key={s.key}
                  active={active}
                  completed={completed}
                  aria-current={active ? "step" : undefined}
                  indicator={
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        minWidth: 32,
                        minHeight: 32,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        bgcolor: `${tone}.solidBg`,
                        color: `${tone}.solidColor`,
                        fontWeight: 600,
                        fontSize: "sm",
                        transition: "all 0.2s ease",
                      }}
                    >
                      {completed ? <CheckIcon fontSize="small" /> : i + 1}
                    </Box>
                  }
                >
                  <Box
                    sx={{
                      py: 1,
                      px: 2,
                      fontWeight: active ? "lg" : "md",
                    }}
                  >
                    {s.label}
                  </Box>
                </Step>
              );
            })}
          </Stepper>

          {/* Step 1: Upload */}
          {step === "upload" && (
            <UploadStep
              documentFile={documentFile}
              sourceFiles={sourceFiles}
              onDocumentDrop={handleDocumentDrop}
              onSourcesDrop={handleSourcesDrop}
              onRemoveSource={removeSourceFile}
              onStart={handleStartQC}
              loading={loading}
              error={qcStatus === "failed" ? errorMessage : null}
            />
          )}

          {/* Step 2: Check */}
          {step === "check" && (
            <QCProgressView
              sections={sortedSections}
              totalSections={totalSections}
              completedSections={
                sortedSections.filter(
                  (s) => s.status === "complete" || s.status === "error",
                ).length
              }
              totalFindings={totalFindings}
              status={
                qcStatus === "completed" || qcStatus === "failed"
                  ? qcStatus
                  : "running"
              }
              errorMessage={errorMessage || undefined}
              reconnectAttempt={reconnect?.attempt}
              reconnectMax={reconnect?.max}
              onRetry={qcStatus === "failed" ? handleRetry : undefined}
            />
          )}
          {step === "check" && qcStatus === "failed" && (
            <Stack direction="row" justifyContent="center" sx={{ mt: 3 }}>
              <Button variant="outlined" color="neutral" onClick={handleReset}>
                Start over
              </Button>
            </Stack>
          )}
          {step === "check" &&
            qcStatus !== "failed" &&
            qcStatus !== "completed" && (
              <Stack direction="row" justifyContent="center" sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  color="danger"
                  onClick={handleStopCheck}
                >
                  Cancel check
                </Button>
              </Stack>
            )}

          {/* Step 3: Review */}
          {step === "review" && (
            <ReviewStep
              sections={sortedSections}
              totalFindings={totalFindings}
              documentName={documentFile?.name ?? "document"}
              onAcceptFinding={handleAcceptFinding}
              onDeclineFinding={handleDeclineFinding}
              onRevertFinding={handleRevertFinding}
              onSubmitFeedback={handleSubmitFeedback}
              onAcceptAll={() => handleDocumentBulkResolve("accepted")}
              onDeclineAll={() => handleDocumentBulkResolve("declined")}
              onRevertAll={() => handleDocumentBulkResolve(null)}
              onReset={handleReset}
              isDocx={
                (documentFile?.name || "").toLowerCase().endsWith(".docx") ||
                (documentFile?.name || "").toLowerCase().endsWith(".doc")
              }
              onDownload={() =>
                qcRunId && downloadTrackedDocument(qcRunId, workspaceId)
              }
              downloadLoading={downloadLoading}
            />
          )}
        </CardContent>
      </Card>
    </Page>
  );
};

export default IndependentQC;
